<template>
  <Transition name="worddef-fade">
    <div
      v-if="visible"
      class="worddef-backdrop"
      @click.self="close"
      @keydown.esc="close"
      role="dialog"
      aria-modal="true"
      :aria-label="STRINGS.definitions.title"
      data-testid="word-definition-modal"
    >
      <div ref="panelRef" class="worddef-panel">
        <!-- Header -->
        <div class="worddef-header">
          <div class="worddef-header-left">
            <div class="worddef-heading">
              <p class="worddef-eyebrow">{{ STRINGS.definitions.title }}</p>
              <h2 class="worddef-word">{{ headingWord }}</h2>
              <!-- Inflected form: collapse the reference record to one note,
                   bolding the parent lemma. -->
              <p v-for="(note, i) in referenceNotes" :key="i" class="worddef-refnote">
                {{ note.lead }}<b v-if="note.lemma">{{ note.lemma }}</b>
              </p>
            </div>
          </div>
          <button
            class="worddef-close"
            :aria-label="STRINGS.definitions.close"
            data-testid="word-definition-close"
            @click="close"
          >
            <AppIcon name="close-x" :size="18" />
          </button>
        </div>

        <!-- Body -->
        <div class="worddef-body">
          <!-- Loading -->
          <div v-if="isLoading" class="worddef-status" data-testid="word-definition-loading">
            <span class="worddef-spinner" aria-hidden="true"></span>
            <p class="worddef-status-text">{{ STRINGS.definitions.loading }}</p>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="worddef-status" data-testid="word-definition-error">
            <AppIcon name="error-x" :size="32" class="worddef-status-icon" />
            <p class="worddef-status-text">{{ error }}</p>
            <button class="worddef-retry" @click="retry">{{ STRINGS.definitions.retry }}</button>
          </div>

          <!-- Coverage gap: the queried word has no record -->
          <div v-else-if="isCoverageGap" class="worddef-status" data-testid="word-definition-empty">
            <span class="worddef-empty-icon">
              <AppIcon name="word-search" :size="30" />
            </span>
            <p class="worddef-status-title">{{ STRINGS.definitions.emptyTitle }}</p>
            <p class="worddef-status-text">{{ STRINGS.definitions.emptyBody(headingWord) }}</p>
          </div>

          <!-- Content -->
          <template v-else-if="definition">
            <WordDefinitionEntry :records="definition.records" />

            <!-- Followed parent lemma(s): an inflected form's lemma, or a sense's
                 form-of parent, rendered inline under a "Definitions · word" label. -->
            <section
              v-if="definition.parents.length"
              class="worddef-parents"
              data-testid="word-definition-parents"
            >
              <div v-for="parent in definition.parents" :key="parent.word" class="worddef-parent">
                <p class="worddef-parent-label">
                  {{ STRINGS.definitions.parentLabel(parent.word) }}
                </p>
                <p v-if="parent.records.length === 0" class="worddef-parent-empty">
                  {{ STRINGS.definitions.parentEmpty(parent.word) }}
                </p>
                <WordDefinitionEntry v-else :records="parent.records" />
              </div>
            </section>
          </template>

          <!-- Source attribution at the foot of any successful fetch (rendered
               entry or coverage gap); omitted while loading or on error. -->
          <DefinitionAttribution v-if="!isLoading && !error && definition" />
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import STRINGS from '@/constants/strings';
import AppIcon from './ui/icons/AppIcon.vue';
import WordDefinitionEntry from './results/WordDefinitionEntry.vue';
import DefinitionAttribution from './results/DefinitionAttribution.vue';
import { useWordDefinition } from '@/composables/useWordDefinition';
import { useFocusTrapping } from '@/composables/useFocusTrapping';
import { isReferenceRecord } from '@/types/word';

const props = defineProps<{
  visible: boolean;
  word: string | null;
}>();

const emit = defineEmits<{
  close: [];
}>();

const { definition, isLoading, error, load, reset } = useWordDefinition();

const panelRef = ref<HTMLElement | null>(null);
const focusTrap = useFocusTrapping({ preventEscapeKeyDisable: true });

const headingWord = computed(() => props.word ?? definition.value?.word ?? '');

// An inflected form resolves to a parent lemma via one or more reference
// records. Collapse each cross-reference into a single note under the word,
// bolding the parent lemma. The dataset's `label` is the whole gloss (e.g.
// "plural of cat") with the lemma also given as `parent`, so bold the trailing
// lemma when the label ends with it; otherwise render the label plain.
const referenceNotes = computed<{ lead: string; lemma: string | null }[]>(() => {
  const def = definition.value;
  if (!def) return [];
  const notes: { lead: string; lemma: string | null }[] = [];
  for (const record of def.records) {
    if (!isReferenceRecord(record)) continue;
    for (const ref of record.form_of) {
      const label = ref.label ?? '';
      const parent = ref.parent ?? '';
      if (parent && label.toLowerCase().endsWith(parent.toLowerCase())) {
        const cut = label.length - parent.length;
        notes.push({ lead: label.slice(0, cut), lemma: label.slice(cut) });
      } else if (label) {
        notes.push({ lead: label, lemma: null });
      }
    }
  }
  return notes;
});

// A coverage gap is a successful fetch whose word has no record.
const isCoverageGap = computed(
  () =>
    !isLoading.value &&
    !error.value &&
    definition.value !== null &&
    definition.value.records.length === 0
);

function close(): void {
  emit('close');
}

function retry(): void {
  if (props.word) load(props.word);
}

// Fetch on open / word change; tear down the trap and state on close.
watch(
  () => [props.visible, props.word] as const,
  async ([visible, word]) => {
    if (visible && word) {
      load(word);
      await nextTick();
      if (panelRef.value) focusTrap.enable(panelRef.value);
    } else if (!visible) {
      focusTrap.disable();
      reset();
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.worddef-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 15, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 9000;
}

.worddef-panel {
  width: 100%;
  max-width: 460px;
  max-height: 90vh;
  overflow-y: auto;
  background: #1e1a14;
  border: 2px solid var(--color-arena-border);
  border-radius: var(--radius-card);
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.6),
    0 8px 32px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

/* --- Header --- */
.worddef-header {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1rem;
  background: #1e1a14;
}

/* Gold headrule beneath the header, matching the design's dm-headrule. */
.worddef-header::after {
  content: '';
  position: absolute;
  left: 1.5rem;
  right: 1.5rem;
  bottom: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--color-energy-primary), transparent);
  opacity: 0.6;
}

.worddef-header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.worddef-heading {
  min-width: 0;
}

.worddef-eyebrow {
  font-family: var(--font-sans);
  font-size: 0.65625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--color-energy-primary);
  margin: 0 0 0.5rem;
}

.worddef-word {
  font-family: var(--font-mono);
  font-size: 2.125rem;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-arena-text-bright);
  margin: 0;
  overflow-wrap: anywhere;
}

.worddef-refnote {
  margin: 0.75rem 0 0;
  font-family: var(--font-sans);
  font-size: 0.78125rem;
  font-style: italic;
  line-height: 1.4;
  color: var(--color-arena-text-muted);
}

.worddef-refnote b {
  font-style: normal;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-energy-primary);
}

.worddef-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: var(--color-arena-text-muted);
  cursor: pointer;
  border-radius: 50%;
  transition:
    color var(--animate-duration-fast) ease,
    background var(--animate-duration-fast) ease;
}

.worddef-close:hover {
  color: var(--color-arena-text-bright);
  background: rgba(255, 255, 255, 0.05);
}

/* --- Body --- */
.worddef-body {
  padding: 1.25rem 1.5rem 1.5rem;
}

/* --- Status states (loading / error / empty) --- */
.worddef-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2.5rem 1rem;
  text-align: center;
}

.worddef-status-icon {
  color: var(--color-arena-text-muted);
}

/* Coverage-gap icon: the word-search mark in the same letterpress tile as the
   look-up affordance. */
.worddef-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 14px;
  background: linear-gradient(180deg, #2a2318, #1e1a12);
  border: 1px solid var(--color-arena-border);
  color: var(--color-arena-text-muted);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 2px 4px rgba(0, 0, 0, 0.4);
}

.worddef-status-title {
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-arena-text-bright);
  margin: 0;
}

.worddef-status-text {
  font-family: var(--font-sans);
  font-size: 0.875rem;
  color: var(--color-arena-text-muted);
  line-height: 1.5;
  max-width: 340px;
  margin: 0;
}

.worddef-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--color-arena-border);
  border-top-color: var(--color-energy-primary);
  border-radius: 50%;
  animation: worddef-spin 700ms linear infinite;
}

@keyframes worddef-spin {
  to {
    transform: rotate(360deg);
  }
}

.worddef-retry {
  margin-top: 0.25rem;
  padding: 0.5rem 1.25rem;
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-arena-text-bright);
  background: transparent;
  border: 1px solid var(--color-arena-border);
  border-radius: var(--radius-button);
  cursor: pointer;
  transition:
    border-color var(--animate-duration-fast) ease,
    background var(--animate-duration-fast) ease;
}

.worddef-retry:hover {
  border-color: var(--color-energy-primary);
  background: rgba(201, 168, 76, 0.06);
}

/* --- Followed parent (lemma) entries --- */
.worddef-parents {
  margin-top: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.worddef-parent {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.worddef-parent-label {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--color-arena-text-dim);
}

.worddef-parent-empty {
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  font-style: italic;
  color: var(--color-arena-text-muted);
  margin: 0;
}

/* --- Transitions --- */
.worddef-fade-enter-active,
.worddef-fade-leave-active {
  transition: opacity 200ms ease;
}

.worddef-fade-enter-active .worddef-panel,
.worddef-fade-leave-active .worddef-panel {
  transition: transform 200ms ease;
}

.worddef-fade-enter-from,
.worddef-fade-leave-to {
  opacity: 0;
}

.worddef-fade-enter-from .worddef-panel,
.worddef-fade-leave-to .worddef-panel {
  transform: scale(0.95);
}

@media (prefers-reduced-motion: reduce) {
  .worddef-fade-enter-active,
  .worddef-fade-leave-active,
  .worddef-fade-enter-active .worddef-panel,
  .worddef-fade-leave-active .worddef-panel {
    transition: none;
  }

  .worddef-spinner {
    animation: none;
  }
}
</style>
